import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'react-use';

import { datadogRum } from '@datadog/browser-rum';

import orderBy from 'lodash/orderBy';
import pick from 'lodash/pick';
import {
    selectFacilityId,
    selectStaffMemberId,
    selectUserCareZoneIds,
} from 'src/store/configSlice';
import {
    Resident,
    sageAdminApi,
    useGetStaffFacilityPreferencesQuery,
    useUnitsSearchQuery,
    useUpdateStaffFacilityPreferencesMutation,
} from 'src/store/sageAdminApi';
import { areSetsEqual } from 'src/utils/setUtils';

export const DebouncePeriodMs = 10_000;

type CareAssignmentsContextValue = {
    addAllToPinned: (residentIds: Set<string>) => void;
    addToPinned: (residentId: string) => void;
    isLoadingResidents: boolean;
    isUpdatingPreferences: boolean;
    pinnedResidentIds: Set<string>;
    pinnedResidents: Resident[];
    removeFromPinned: (residentId: string) => void;
    replaceAllPinned: (residentIds: Set<string>) => void;
    togglePinnedResidentId: (residentId: string) => void;
};

export const CareAssignmentsContext = createContext<CareAssignmentsContextValue>({
    addAllToPinned: () => {
        /* no-op */
    },
    addToPinned: () => {
        /* no-op */
    },
    isLoadingResidents: false,
    isUpdatingPreferences: false,
    pinnedResidentIds: new Set(),
    pinnedResidents: [],
    removeFromPinned: () => {
        /* no-op */
    },
    replaceAllPinned: () => {
        /* no-op */
    },
    togglePinnedResidentId: () => {
        /* no-op */
    },
});

export function CareAssignmentsProvider({ children }: PropsWithChildren) {
    const dispatch = useDispatch();

    const facilityId = useSelector(selectFacilityId);
    const staffMemberId = useSelector(selectStaffMemberId);
    const careZoneIds = useSelector(selectUserCareZoneIds);

    const [localState, setLocalState] = useState<Set<string> | undefined>(undefined);

    const [updatePreferences, { isLoading: isUpdatingPreferences }] =
        useUpdateStaffFacilityPreferencesMutation();

    const {
        data: facilityPreferences,
        isFetching,
        isLoading,
    } = useGetStaffFacilityPreferencesQuery(
        { staffMemberId, facilityId },
        { skip: !staffMemberId || !facilityId }
    );

    // Sync local state with server state when it is loaded from the server
    useEffect(() => {
        if (!isLoading && !isFetching) {
            setLocalState(new Set(facilityPreferences?.pinnedResidentIds ?? []));
        }
    }, [isLoading, isFetching, facilityPreferences]);

    const pinnedResidentIds = useMemo(() => localState ?? new Set<string>(), [localState]);

    const { units, isLoading: isLoadingUnits } = useUnitsSearchQuery(
        { unitSearchRequest: { facilityIds: [facilityId], careZoneIds } },
        {
            skip: !facilityId,
            selectFromResult: (results) => ({ ...results, units: results?.data?.units ?? [] }),
        }
    );

    const pinnedResidents = useMemo(
        () =>
            orderBy(
                units.reduce((acc: Resident[], unit) => {
                    if (!unit?.currentOccupants) return acc;

                    const unitProperties = pick(unit, [
                        'careType',
                        'displayName',
                        'building',
                        'id',
                        'phoneNumber',
                        'wing',
                        'floor',
                    ]);

                    // NOTE: exclude archited and on-hold residents from pinned list
                    const pinned = unit.currentOccupants
                        .filter(
                            (occupant) =>
                                !occupant.isOnHold &&
                                !occupant.isArchived &&
                                pinnedResidentIds.has(occupant.id!)
                        )
                        .map((occupant) => ({ ...occupant, unit: unitProperties }));

                    return [...acc, ...pinned];
                }, []),
                ['unit.displayName', 'preferredName', 'firstName', 'lastName']
            ),
        [units, pinnedResidentIds]
    );

    const updateServerState = useCallback(
        async (residentIdsArray: string[]) => {
            try {
                await updatePreferences({
                    staffMemberId,
                    facilityId,
                    updateStaffFacilityPreferencesRequest: {
                        pinnedResidentIds: residentIdsArray,
                    },
                }).unwrap();
                dispatch(sageAdminApi.util.invalidateTags(['careTask']));
            } catch (error) {
                datadogRum.addError(error, {
                    message: 'Error updating pinned resident ids',
                    staffMemberId,
                    facilityId,
                    residentIdsArray,
                });
            }
        },
        [updatePreferences, staffMemberId, facilityId, dispatch]
    );

    // Debounced sync to backend whenever local state changes
    useDebounce(
        async () => {
            if (localState === undefined) return;
            const backendSet = new Set(facilityPreferences?.pinnedResidentIds ?? []);
            if (areSetsEqual(backendSet, localState)) {
                console.debug('Not updating backend state as it is the same as the current state');
            } else {
                await updateServerState(Array.from(localState));
            }
        },
        DebouncePeriodMs,
        [localState, facilityPreferences, updateServerState]
    );

    const addToPinned = useCallback((residentId: string) => {
        if (residentId) {
            setLocalState((prev) => {
                const current = prev ?? new Set<string>();
                if (current.has(residentId)) return current;
                return new Set([...current, residentId]);
            });
        }
    }, []);

    const addAllToPinned = useCallback((residentIds: Set<string>) => {
        if (residentIds && residentIds.size > 0) {
            setLocalState((prev) => {
                const current = prev ?? new Set<string>();
                const updated = new Set(current);
                residentIds.forEach((id) => updated.add(id));
                return updated;
            });
        }
    }, []);

    const replaceAllPinned = useCallback(
        (residentIds: Set<string>) => {
            const newSet = new Set(residentIds);
            setLocalState(newSet);
            updateServerState(Array.from(newSet));
        },
        [updateServerState]
    );

    const removeFromPinned = useCallback((residentId: string) => {
        if (residentId) {
            setLocalState((prev) => {
                const current = prev ?? new Set<string>();
                if (!current.has(residentId)) return current;
                const updated = new Set(current);
                updated.delete(residentId);
                return updated;
            });
        }
    }, []);

    const togglePinnedResidentId = useCallback((residentId: string) => {
        setLocalState((prev) => {
            const current = prev ?? new Set<string>();
            const updated = new Set(current);
            if (updated.has(residentId)) {
                updated.delete(residentId);
            } else {
                updated.add(residentId);
            }
            return updated;
        });
    }, []);

    const value = useMemo(
        () => ({
            addAllToPinned,
            addToPinned,
            isLoadingResidents: isLoadingUnits,
            isUpdatingPreferences,
            pinnedResidentIds,
            pinnedResidents,
            removeFromPinned,
            replaceAllPinned,
            togglePinnedResidentId,
        }),
        [
            addAllToPinned,
            addToPinned,
            isLoadingUnits,
            isUpdatingPreferences,
            pinnedResidentIds,
            pinnedResidents,
            removeFromPinned,
            replaceAllPinned,
            togglePinnedResidentId,
        ]
    );

    return (
        <CareAssignmentsContext.Provider value={value}>{children}</CareAssignmentsContext.Provider>
    );
}

export function useCareAssignments(): CareAssignmentsContextValue {
    const context = useContext(CareAssignmentsContext);
    if (context === undefined) {
        throw new Error('useCareAssignments must be used within a CareAssignmentsProvider');
    }
    return context;
}

export default CareAssignmentsProvider;
